import React, { useState } from 'react'
import { connect } from 'react-redux'
import { makeStyles } from '@material-ui/core'
import Dialog from '@mui/material/Dialog'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import CircularProgress from '@material-ui/core/CircularProgress'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import ErrorIcon from '@material-ui/icons/Error'
import xImage from '../../../../assets/images/Navigation.png'
import ComponentStepper from '../../Creator/ComponentStepper'
import ChooserComponent from './ChooserComponent'
import MultipleStudentAddComponent from '../../../../Components/MultipleStudentAddModal/MultipleStudentAddComponent'
import { FormattedMessage } from 'react-intl'
import { organizationActions } from '../../../../_state/actions'

const useStyles = makeStyles((theme) => ({
    mainContainer: {
        display: 'flex',
        flexDirection: 'column',
        padding: 32,
    },
    closeIcon: {
        width: 24,
        height: 24,
        alignItems: 'flex-end',
        justifyContent: 'flex-end',
        alignSelf: 'flex-end',
        marginRight: 16,
        marginTop: 16,
        display: 'flex',
        cursor: 'pointer',
    },
    resultSummary: {
        backgroundColor: '#f5f5f5',
        borderRadius: 8,
        padding: 16,
        marginTop: 16,
    },
    resultRow: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '4px 0',
    },
    errorTable: {
        width: '100%',
        borderCollapse: 'collapse',
        marginTop: 12,
    },
    errorTableHeader: {
        backgroundColor: '#f5f5f5',
        textAlign: 'left',
        padding: '8px 12px',
        fontWeight: 600,
        fontSize: 13,
    },
    errorTableCell: {
        padding: '6px 12px',
        borderBottom: '1px solid #eee',
        fontSize: 13,
    },
}))

const AddStudentsToClassroomModal = ({ open, onClose, classrooms, onAddStudentsToClassroom, organization, dispatch }) => {
    const classes = useStyles()

    const [selectedClassroom, setSelectedClassroom] = useState(null)
    const [addStudentRows, setAddStudentRows] = React.useState([])
    const [hasAnyValidEmails, setHasAnyValidEmails] = React.useState(false)

    const addingStudents = organization.addingStudents
    const inviteResult = organization.studentInviteResult

    const listOfClassrooms = classrooms.map((item) => {
        return { text: item.name, item: item }
    })

    const onChooseClassroom = (classroom) => {
        setSelectedClassroom(classroom)
    }

    const handleStudentRowsAreUpdated = (studentRows) => {
        setAddStudentRows(studentRows)
        setHasAnyValidEmails(studentRows.filter((x) => x.validEmail).length > 0)
    }

    const editCourseStepperComponents = [
        {
            component: (
                <MultipleStudentAddComponent
                    onStudentRowsChanged={handleStudentRowsAreUpdated}
                    onHasAnyValidEmails={(value) => setHasAnyValidEmails(value)}
                    showTitle={true}
                    title={
                        <FormattedMessage
                            id="dashboard.classroom.students.invitemodal.title"
                            defaultMessage="Invite new students"
                        />
                    }
                    description={
                        <FormattedMessage
                            id="dashboard.classroom.students.invitemodal.description"
                            defaultMessage="Enter emails for user to invite. Use tab key to navigate to the next field. You can paste in space or comma separated lists (eg. from a Excel column). The name field will only be shown in the dashboard and not shared with the user."
                        />
                    }
                    initialStudentRows={addStudentRows}
                />
            ),
            stepCondition: hasAnyValidEmails,
        },
        {
            component: (
                <ChooserComponent
                    listToChooseFrom={listOfClassrooms}
                    onSetChosenOption={onChooseClassroom}
                    defaultOption={selectedClassroom}
                    showTitle={true}
                    title={
                        <FormattedMessage
                            id="dashboard.classroom.students.choose.classroom.title"
                            defaultMessage="Choose a classroom"
                        />
                    }
                    description={
                        <FormattedMessage
                            id="dashboard.classroom.students.choose.classroom.description"
                            defaultMessage="Users in Capeesh must be a part of a classroom to make sure they get assigned the courses in that classroom."
                        />
                    }
                />
            ),
            stepCondition: selectedClassroom,
        },
    ]

    const handleAddStudentsToClassroom = () => {
        if (!selectedClassroom) {
            return
        }
        if (addStudentRows.length === 0) {
            return
        }

        onAddStudentsToClassroom(addStudentRows, selectedClassroom.id)
    }

    const handleClose = () => {
        dispatch(organizationActions.resetStudentInvite())
        setSelectedClassroom(null)
        onClose()
    }

    return (
        <Dialog open={open} maxWidth={'md'} onClose={handleClose}>
            <div className={classes.mainContainer}>
                <img onClick={() => handleClose()} src={xImage} className={classes.closeIcon} />

                {!inviteResult && !addingStudents && (
                    <ComponentStepper
                        stepperComponents={editCourseStepperComponents}
                        onFinishStepper={handleAddStudentsToClassroom}
                        onCancel={handleClose}
                    />
                )}

                {addingStudents && (
                    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                        <CircularProgress size={40} />
                        <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
                            Inviting users, please wait...
                        </Typography>
                    </div>
                )}

                {inviteResult && (
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                            <CheckCircleIcon style={{ color: '#4caf50' }} />
                            <Typography variant="h6" style={{ fontWeight: 600 }}>
                                Invite Complete
                            </Typography>
                        </div>

                        <div className={classes.resultSummary}>
                            <div className={classes.resultRow}>
                                <Typography variant="body2">Total users processed</Typography>
                                <Typography variant="body2" style={{ fontWeight: 600 }}>
                                    {inviteResult.totalUsers}
                                </Typography>
                            </div>
                            <div className={classes.resultRow}>
                                <Typography variant="body2">New users invited</Typography>
                                <Typography variant="body2" style={{ fontWeight: 600, color: '#4caf50' }}>
                                    {inviteResult.usersInvited}
                                </Typography>
                            </div>
                            <div className={classes.resultRow}>
                                <Typography variant="body2">Users already in organization</Typography>
                                <Typography variant="body2" style={{ fontWeight: 600 }}>
                                    {inviteResult.usersAlreadyInOrganization}
                                </Typography>
                            </div>
                        </div>

                        {inviteResult.errors && inviteResult.errors.length > 0 && (
                            <div style={{ marginTop: 16 }}>
                                <Typography variant="body2" style={{ fontWeight: 600, color: '#f44336', marginBottom: 8 }}>
                                    <ErrorIcon style={{ fontSize: 16, verticalAlign: 'middle', marginRight: 4 }} />
                                    {inviteResult.errors.length} user(s) had issues:
                                </Typography>
                                <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                                    <table className={classes.errorTable}>
                                        <thead>
                                            <tr>
                                                <th className={classes.errorTableHeader}>Email</th>
                                                <th className={classes.errorTableHeader}>Error</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {inviteResult.errors.map((err, idx) => (
                                                <tr key={idx}>
                                                    <td className={classes.errorTableCell}>{err.email}</td>
                                                    <td className={classes.errorTableCell}>{err.errorMessage}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        <div style={{ marginTop: 24, textAlign: 'right' }}>
                            <Button onClick={handleClose} variant="contained" color="primary">
                                <FormattedMessage id="globalwords.close" defaultMessage="Close" />
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </Dialog>
    )
}

AddStudentsToClassroomModal.propTypes = {}

const mapStateToProps = (state) => {
    return {
        organization: state.organization,
    }
}

export default connect(mapStateToProps)(AddStudentsToClassroomModal)
